import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import {
  AdminConfig,
  AdminUser,
  getConfigs,
  createConfig,
  updateConfig,
  deleteConfig,
  getUsers,
} from "@services/adminService";

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 720px;
`;

const NewButton = styled(Button)`
  width: auto;
  min-width: 130px;
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
  max-width: 720px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 12px;
`;

const CardTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 15px;
  font-weight: bold;
  color: #494c5b;
`;

const KeyBadge = styled.code`
  font-family: "SF Mono", Menlo, monospace;
  font-size: 13px;
  color: #494c5b;
  background: #eef1f2;
  padding: 3px 10px;
  border-radius: 6px;
`;

const StatusBadge = styled.span<{ all: boolean }>`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: bold;
  padding: 3px 10px;
  border-radius: 999px;
  color: ${({ all }) => (all ? "#1f7a1f" : "#7a7f82")};
  background: ${({ all }) => (all ? "#e3f6e3" : "#ececec")};
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Label = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #899093;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #494c5b;
  cursor: pointer;
`;

const PickerBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  max-width: 480px;
`;

const UserList = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const UserRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  cursor: pointer;
  border-top: 1px solid #f0f2f3;
  &:first-of-type {
    border-top: none;
  }
  &:hover {
    background: #f6f8f9;
  }
`;

const UserEmail = styled.span`
  color: #899093;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 38px;
  border: none;
  border-bottom: 1px solid #f0f2f3;
  padding: 0 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  outline: none;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 110px;
`;

const DeleteButton = styled(ActionButton)`
  background: #fff;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  & path {
    fill: #e74c3c;
  }
  :hover {
    background: #fdecea;
    color: #e74c3c;
    border: 1px solid #e74c3c;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #899093;
  max-width: 720px;
  padding: 8px 0;
`;

/**
 * Searchable multi-select of users, driven by a Set of selected ids.
 * `pinnedIds` (the config's saved allowlist) float to the top, keeping the
 * created-at order within each group. Pinning uses the saved list rather than
 * `selected` so rows don't jump while boxes are being ticked.
 */
const UserPicker: React.FC<{
  users: AdminUser[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  pinnedIds?: string[];
}> = ({ users, selected, onChange, disabled, pinnedIds }) => {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const ordered = useMemo(() => {
    if (!pinnedIds?.length) return users;
    const pinned = new Set(pinnedIds);
    return [
      ...users.filter((u) => pinned.has(u.id)),
      ...users.filter((u) => !pinned.has(u.id)),
    ];
  }, [users, pinnedIds]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name ?? "").toLowerCase().includes(q)
    );
  }, [ordered, search]);

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <PickerBox>
      <SearchInput
        placeholder="Search users by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={disabled}
      />
      <UserList>
        {filtered.map((u) => (
          <UserRow key={u.id}>
            <input
              type="checkbox"
              checked={selectedSet.has(u.id)}
              onChange={() => toggle(u.id)}
              disabled={disabled}
            />
            <span>{u.name || u.email}</span>
            {u.name ? <UserEmail>· {u.email}</UserEmail> : null}
          </UserRow>
        ))}
        {filtered.length === 0 && (
          <UserRow as="div">No matching users</UserRow>
        )}
      </UserList>
    </PickerBox>
  );
};

// A config being edited/created in the UI (draft mirrors the server shape).
type Draft = { key: string; name: string; enabledForAll: boolean; userIds: string[] };

const AdminConfigsTab: React.FC = () => {
  const [configs, setConfigs] = useState<AdminConfig[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState<Draft | null>(null);
  // Local edits per config id, applied on Save.
  const [edits, setEdits] = useState<Record<string, Draft>>({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, userRes] = await Promise.all([
        getConfigs(),
        getUsers({ limit: 1000 }),
      ]);
      setConfigs(cfgRes.configs);
      setUsers(userRes.users);
      setEdits({});
    } catch (e) {
      // toast already shown by api client
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const draftFor = (c: AdminConfig): Draft =>
    edits[c.id] ?? {
      key: c.key,
      name: c.name,
      enabledForAll: c.enabledForAll,
      userIds: c.userIds,
    };

  const patchDraft = (c: AdminConfig, patch: Partial<Draft>) =>
    setEdits((prev) => ({ ...prev, [c.id]: { ...draftFor(c), ...patch } }));

  const handleCreate = async () => {
    if (!newDraft) return;
    const key = newDraft.key.trim();
    if (!/^[a-z0-9_]+$/.test(key)) {
      toast.error("Key must be lowercase letters, numbers and underscores");
      return;
    }
    if (!newDraft.name.trim()) {
      toast.error("Add a name");
      return;
    }
    setCreating(true);
    try {
      await createConfig({
        key,
        name: newDraft.name.trim(),
        enabledForAll: newDraft.enabledForAll,
        userIds: newDraft.userIds,
      });
      setNewDraft(null);
      await fetchAll();
      toast.success("Config created");
    } catch (e) {
      // toast already shown
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (c: AdminConfig) => {
    const d = draftFor(c);
    if (!d.name.trim()) {
      toast.error("Add a name");
      return;
    }
    setBusyId(c.id);
    try {
      await updateConfig(c.id, {
        name: d.name.trim(),
        enabledForAll: d.enabledForAll,
        userIds: d.userIds,
      });
      await fetchAll();
      toast.success("Config saved");
    } catch (e) {
      // toast already shown
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (c: AdminConfig) => {
    if (!window.confirm(`Delete config "${c.name}" (${c.key})?`)) return;
    setBusyId(c.id);
    try {
      await deleteConfig(c.id);
      setConfigs((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Config deleted");
    } catch (e) {
      // toast already shown
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  return (
    <div>
      <TopBar>
        <Text size={14} color="#899093">
          {configs.length} config{configs.length === 1 ? "" : "s"} · gate a feature
          behind a key, then check it with settingsStore.hasConfig("key")
        </Text>
        <NewButton
          onClick={() =>
            setNewDraft(
              newDraft
                ? null
                : { key: "", name: "", enabledForAll: false, userIds: [] }
            )
          }
        >
          {newDraft ? "Cancel" : "+ New config"}
        </NewButton>
      </TopBar>

      {newDraft && (
        <Card>
          <CardHeader>
            <CardTitle>New config</CardTitle>
          </CardHeader>
          <FieldWrap>
            <Label>Key (code identifier — immutable)</Label>
            <Input
              value={newDraft.key}
              onChange={(e) =>
                setNewDraft({ ...newDraft, key: e.target.value.toLowerCase() })
              }
              placeholder="e.g. prescription_maps_v2"
              maxLength={200}
            />
          </FieldWrap>
          <FieldWrap>
            <Label>Name</Label>
            <Input
              value={newDraft.name}
              onChange={(e) => setNewDraft({ ...newDraft, name: e.target.value })}
              placeholder="e.g. Prescription maps v2"
              maxLength={200}
            />
          </FieldWrap>
          <FieldWrap>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={newDraft.enabledForAll}
                onChange={(e) =>
                  setNewDraft({ ...newDraft, enabledForAll: e.target.checked })
                }
              />
              Enabled for all users (ignores the allowlist below)
            </CheckboxRow>
          </FieldWrap>
          <FieldWrap>
            <Label>Users ({newDraft.userIds.length} selected)</Label>
            <UserPicker
              users={users}
              selected={newDraft.userIds}
              onChange={(ids) => setNewDraft({ ...newDraft, userIds: ids })}
              disabled={newDraft.enabledForAll}
            />
          </FieldWrap>
          <Actions>
            <ActionButton onClick={handleCreate} disabled={creating}>
              {creating ? "Creating…" : "Create"}
            </ActionButton>
          </Actions>
        </Card>
      )}

      {configs.length === 0 && !newDraft && (
        <EmptyState>
          No configs yet. Click “+ New config”, give it a key + name, add yourself to
          the allowlist, then gate your feature behind that key.
        </EmptyState>
      )}

      {configs.map((c) => {
        const d = draftFor(c);
        const busy = busyId === c.id;
        return (
          <Card key={c.id}>
            <CardHeader>
              <KeyBadge>{c.key}</KeyBadge>
              <StatusBadge all={d.enabledForAll}>
                {d.enabledForAll
                  ? "All users"
                  : `${d.userIds.length} user${d.userIds.length === 1 ? "" : "s"}`}
              </StatusBadge>
            </CardHeader>

            <FieldWrap>
              <Label>Name</Label>
              <Input
                value={d.name}
                onChange={(e) => patchDraft(c, { name: e.target.value })}
                maxLength={200}
              />
            </FieldWrap>

            <FieldWrap>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={d.enabledForAll}
                  onChange={(e) =>
                    patchDraft(c, { enabledForAll: e.target.checked })
                  }
                />
                Enabled for all users (ignores the allowlist below)
              </CheckboxRow>
            </FieldWrap>

            <FieldWrap>
              <Label>Users ({d.userIds.length} selected)</Label>
              <UserPicker
                users={users}
                selected={d.userIds}
                pinnedIds={c.userIds}
                onChange={(ids) => patchDraft(c, { userIds: ids })}
                disabled={d.enabledForAll}
              />
            </FieldWrap>

            <Actions>
              <ActionButton onClick={() => handleSave(c)} disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </ActionButton>
              <DeleteButton onClick={() => handleDelete(c)} disabled={busy}>
                Delete
              </DeleteButton>
            </Actions>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminConfigsTab;
